import { SDK, SDK_GLOBAL_NAME } from "@frontend/roz-sdk-core";

import type { LoaderConfig } from "@/loader-config";
import { splitLoaderConfig } from "@/loader-config";
import { SDK_LOADER_CONFIG_SCRIPT_SELECTOR } from "@/loader-constants";
import { mountShell } from "@/mountRoot";

declare global {
  interface Window {
    // Полноценный SDK со всеми модулями, опубликованный синхронно на этапе
    // выполнения loader'а — доступен потребителю уже в onLoad скрипта.
    RozSDK?: SDK;
    __RozSDKLoaderStarted?: boolean;
  }
}

// В UMD-бандле (браузер) process отсутствует, но часть зависимостей читает
// process.env — подкладываем минимальную заглушку.
if (typeof process === "undefined") {
  Reflect.set(globalThis, "process", { env: {} });
}

// Флаг «loader уже стартовал» держим на window (а не в модульной переменной),
// чтобы второй UMD-тег на странице не запускал init повторно.
const LOADER_STARTED_FLAG = "__RozSDKLoaderStarted";

export function bootLoader(): void {
  const w = globalThis.window;
  if (w?.[LOADER_STARTED_FLAG]) {
    console.warn(`${SDK_GLOBAL_NAME} уже инициализирован`);
    return;
  }
  if (w) {
    w[LOADER_STARTED_FLAG] = true;
  }

  const scriptElement = document.querySelector(
    SDK_LOADER_CONFIG_SCRIPT_SELECTOR
  );
  if (!scriptElement) {
    console.error("SDK loader: не найден скрипт с data-sdk-config");
    return;
  }

  let config: LoaderConfig;
  try {
    config = JSON.parse(scriptElement.getAttribute("data-sdk-config") ?? "{}");
  } catch (error) {
    console.error("SDK loader: ошибка парсинга config", error);
    return;
  }

  const { sdkConfig } = splitLoaderConfig(config);

  // Полноценный SDK создаётся синхронно — все модули и события доступны сразу,
  // до завершения async init. Потребитель, обращающийся к RozSDK.events /
  // RozSDK.analytics в onLoad, видит реальные методы. Монтируем shell по
  // готовности фоновой инициализации.
  const sdk = SDK.create(sdkConfig);
  if (w) {
    w[SDK_GLOBAL_NAME] = sdk;
  }

  sdk
    .ready()
    .then(() => {
      SDK.log("loader", "SDK initialized", {
        version: sdk.version,
        config: sdk.config,
      });
      mountShell(sdk, config);
    })
    .catch((error) =>
      console.error(`${SDK_GLOBAL_NAME} ошибка при инициализации`, error)
    );
}

if (typeof document !== "undefined") {
  bootLoader();
}
